// API URL'i
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// Placeholder resim URL'i
const PLACEHOLDER_IMAGE = 'https://via.placeholder.com/300x300?text=Ürün+Görseli';

// Kullanıcı ID'si için bir yardımcı fonksiyon
// Gerçek uygulamada bu, oturum yönetimi veya kimlik doğrulama sisteminize bağlı olacaktır
export const getUserId = (): string => {
  // Kullanıcı kimliği localStorage'da yoksa, rastgele bir kimlik oluştur ve kaydet
  let userId = localStorage.getItem('user_id');
  if (!userId) {
    userId = 'user_' + Math.random().toString(36).substring(2, 15);
    localStorage.setItem('user_id', userId);
  }
  return userId;
};

// Sepet API'si
export const CartAPI = {
  // Sepeti getir
  getCart: async () => {
    try {
      const response = await fetch(`${API_URL}/cart?user_id=${getUserId()}`);
      if (!response.ok) {
        throw new Error('Sepet getirilemedi');
      }
      return await response.json();
    } catch (error) {
      throw error;
    }
  },

  // Sepete ürün ekle
  addToCart: async (product: any) => {
    try {
      // Resim URL'sini kontrol et, yoksa placeholder kullan
      const productImage = product.image && product.image.trim() !== '' ? product.image : PLACEHOLDER_IMAGE;
      
      // Fiyatı sayıya dönüştür
      let price = 0;
      if (typeof product.price === 'string') {
        price = parseFloat(product.price.replace(/[^\d.]/g, ''));
      } else if (typeof product.price === 'number') {
        price = product.price;
      }
      
      const response = await fetch(`${API_URL}/cart/add`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          product_id: product.id,
          product_name: product.name || 'Ürün',
          product_image: productImage,
          price: price || 0,
          quantity: 1,
          user_id: getUserId(),
        }),
      });

      if (!response.ok) {
        throw new Error('Ürün sepete eklenemedi');
      }
      return await response.json();
    } catch (error) {
      throw error;
    }
  },

  // Sepetteki ürün miktarını güncelle
  updateCartItem: async (itemId: number, quantity: number) => {
    try {
      const response = await fetch(`${API_URL}/cart/update/${itemId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ quantity }),
      });

      if (!response.ok) {
        throw new Error('Sepet güncellenemedi');
      }
      return await response.json();
    } catch (error) {
      throw error;
    }
  },

  // Sepetten ürün kaldır
  removeFromCart: async (itemId: number) => {
    try {
      const response = await fetch(`${API_URL}/cart/remove/${itemId}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        throw new Error('Ürün sepetten kaldırılamadı');
      }
      return await response.json();
    } catch (error) {
      throw error;
    }
  },

  // Sepeti temizle
  clearCart: async () => {
    try {
      const response = await fetch(`${API_URL}/cart/clear?user_id=${getUserId()}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        throw new Error('Sepet temizlenemedi');
      }
      return await response.json();
    } catch (error) {
      throw error;
    }
  },
};

// Email verification API'si
export const VerificationAPI = {
  // Doğrulama kodu gönder
  sendVerificationCode: async (email: string, language: string = 'tr') => {
    try {
      const response = await fetch(`${API_URL}/send-verification`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: email,
          user_id: getUserId(),
          language: language,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Doğrulama kodu gönderilemedi');
      }
      return await response.json();
    } catch (error) {
      throw error;
    }
  },

  // Doğrulama kodunu kontrol et
  verifyCode: async (code: string) => {
    try {
      const response = await fetch(`${API_URL}/verify-code`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          code: code,
          user_id: getUserId(),
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Doğrulama kodu geçersiz');
      }
      return await response.json();
    } catch (error) {
      throw error;
    }
  },
};

// Order API'si
export const OrderAPI = {
  // Sipariş oluştur
  createOrder: async (orderData: any) => {
    try {
      const response = await fetch(`${API_URL}/create-order`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...orderData,
          user_id: getUserId(),
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Sipariş oluşturulamadı');
      }
      return await response.json();
    } catch (error) {
      throw error;
    }
  },

  // Sipariş durumunu getir
  getOrderStatus: async (customerId: string) => {
    try {
      const response = await fetch(`${API_URL}/order-status/${customerId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Sipariş durumu alınamadı');
      }
      return await response.json();
    } catch (error) {
      throw error;
    }
  },

  // Admin - Tüm siparişleri getir
  getAllOrders: async () => {
    try {
      const token = localStorage.getItem('admin_token');
      if (!token) {
        throw new Error('Yetkilendirme gerekli');
      }

      const response = await fetch(`${API_URL}/admin/orders`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Siparişler alınamadı');
      }
      return await response.json();
    } catch (error) {
      throw error;
    }
  },

  // Admin - Sipariş durumunu güncelle
  updateOrderStatus: async (orderId: number, status: string, adminNotes?: string, cargoCompany?: string, cargoTrackingNumber?: string) => {
    try {
      const token = localStorage.getItem('admin_token');
      if (!token) {
        throw new Error('Yetkilendirme gerekli');
      }

      const response = await fetch(`${API_URL}/admin/orders/${orderId}/status`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          status,
          admin_notes: adminNotes || '',
          cargo_company: cargoCompany,
          cargo_tracking_number: cargoTrackingNumber,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Sipariş durumu güncellenemedi');
      }
      return await response.json();
    } catch (error) {
      throw error;
    }
  },

  // Admin - Session doğrulama
  verifyAdminSession: async () => {
    try {
      const token = localStorage.getItem('admin_token');
      if (!token) {
        throw new Error('Yetkilendirme gerekli');
      }

      const response = await fetch(`${API_URL}/admin/verify-session`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Session geçersiz');
      }
      return await response.json();
    } catch (error) {
      throw error;
    }
  },

  // Admin - Çıkış
  adminLogout: async () => {
    try {
      const token = localStorage.getItem('admin_token');
      if (!token) {
        return;
      }

      await fetch(`${API_URL}/admin/logout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      localStorage.removeItem('admin_token');
      localStorage.removeItem('admin_expires_at');
    } catch (error) {
      localStorage.removeItem('admin_token');
      localStorage.removeItem('admin_expires_at');
    }
  },
};

export default {
  CartAPI,
  VerificationAPI,
  OrderAPI,
};
